import React, { useEffect } from "react";
import MainLayout from "../../Layout/mainLayout";
import { Link, useParams } from "react-router-dom";
import "./viewTeamMember.css";
import { viewTeamMember } from "../../Redux/Actions/teamMembersAction";
import { useDispatch, useSelector } from "react-redux";
import { ToFirstCapital } from "../../Utils/helpers";

const ViewTeamMember = () => {
  const dispatch = useDispatch();
  const { id } = useParams();

  const { selectedMember } = useSelector((state) => state.teamMembers || {});
  const m = selectedMember?.data || {};

  const { admin } = useSelector((state) => state.admin || {});
  const accessCode = admin?.data?.access_code || [];
  const canAdd = Array.isArray(accessCode) && accessCode.includes('add');
  const canEdit = Array.isArray(accessCode) && accessCode.includes('edit');


  useEffect(() => {
    if (id) dispatch(viewTeamMember({ id: id }));
  }, [dispatch, id]);

  // ── Reusable read-only field ──
  const Field = ({ label, value }) => (
    <div className="vtm-field">
      <label className="vtm-label">{label}</label>
      <div className="vtm-value">
        {value || <span className="vtm-value--empty">—</span>}
      </div>
    </div>
  );

  const isBlocked = m.is_blocked;

  return (
    <MainLayout>
      <div className="vtm-wrapper">

        {/* ── Header ── */}
        <div className="vtm-header">
          <div className="vtm-header-left">
            <div className="vtm-header-icon">
              <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <path d="M17 21v-2a4 4 0 0 0-4-4H6a4 4 0 0 0-4 4v2" />
                <circle cx="9" cy="7" r="4" />
                <path d="M23 21v-2a4 4 0 0 0-3-3.87" />
                <path d="M16 3.13a4 4 0 0 1 0 7.75" />
              </svg>
            </div>
            <div>
              <h1 className="vtm-title">View Team Member</h1>
              <p className="vtm-subtitle">Detailed profile of the team member</p>
            </div>
          </div>
          <div className="vtm-header-actions">
            {canEdit &&
              <Link to={`/team/edit/${id}`} className="vtm-btn vtm-btn--outline">
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7" />
                  <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z" />
                </svg>
                Edit Member
              </Link>
            }
            <Link to="/team" className="vtm-btn vtm-btn--outline">
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2" />
                <circle cx="9" cy="7" r="4" />
                <path d="M23 21v-2a4 4 0 0 0-3-3.87" />
                <path d="M16 3.13a4 4 0 0 1 0 7.75" />
              </svg>
              View Team
            </Link>
          </div>
        </div>

        <div className="vtm-card">

          {/* ───── SECTION 0: PERSONAL INFO ───── */}
          <div className="vtm-section">
            <div className="vtm-section-header">
              <span className="vtm-section-badge">01</span>
              <h2 className="vtm-section-title">Personal Information</h2>
            </div>

            {/* Avatar Row */}
            <div className="vtm-avatar-row">
              <div className="vtm-avatar-wrap">
                {m.profile_image ? (
                  <img src={m.profile_image} alt="Profile" className="vtm-avatar-img" />
                ) : (
                  <div className="vtm-avatar-placeholder">
                    <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
                      <circle cx="12" cy="8" r="4" />
                      <path d="M4 20c0-4 3.6-7 8-7s8 3 8 7" />
                    </svg>
                  </div>
                )}
              </div>
              <div className="vtm-avatar-info">
                <p className="vtm-avatar-name">{m.name || "—"}</p>
                <p className="vtm-avatar-email">{m.email || "—"}</p>
                <span className={`vtm-status-badge ${isBlocked ? "vtm-status-badge--blocked" : "vtm-status-badge--active"}`}>
                  <span className={`vtm-status-dot ${isBlocked ? "vtm-status-dot--blocked" : "vtm-status-dot--active"}`} />
                  {isBlocked ? "Blocked" : "Active"}
                </span>
              </div>
            </div>

            <div className="vtm-grid vtm-grid-2">
              <Field label="Full Name" value={m.name} />
              <Field label="Email Address" value={m.email} />
              <Field label="Country Code" value={m.country_code} />
              <Field label="Phone Number" value={m.contact_number} />
              <Field label="Gender" value={m.gender ? ToFirstCapital(m.gender) : ""} />
              <Field
                label="Date of Birth"
                value={m.dob ? new Date(m.dob).toLocaleDateString("en-IN", { day: "2-digit", month: "short", year: "numeric" }) : ""}
              />
              <Field label="Age" value={m.age} />
            </div>
          </div>

          {/* ───── SECTION 1: WORK DETAILS ───── */}
          <div className="vtm-section">
            <div className="vtm-section-header">
              <span className="vtm-section-badge">02</span>
              <h2 className="vtm-section-title">Work Details</h2>
            </div>
            <div className="vtm-grid vtm-grid-2">
              <Field label="Role" value={m.role ? ToFirstCapital(m.role) : ""} />
              <Field label="Designation" value={m.designation} />
              <Field label="Department" value={m.department ? ToFirstCapital(m.department) : ""} />
              <Field label="Time Zone" value={m.timezone} />
              <Field
                label="Joining Date"
                value={m.joining_date ? new Date(m.joining_date).toLocaleDateString("en-IN", { day: "2-digit", month: "short", year: "numeric" }) : ""}
              />
              <Field label="Experience (years)" value={m.experience} />
              <Field label="Qualification" value={m.qualifications?.[0]?.degree} />
              <Field label="Branch Address" value={m.branch_address} />
            </div>
          </div>

          {/* ───── SECTION 2: ADDRESS ───── */}
          <div className="vtm-section">
            <div className="vtm-section-header">
              <span className="vtm-section-badge">03</span>
              <h2 className="vtm-section-title">Address Details</h2>
            </div>
            <div className="vtm-grid vtm-grid-2">
              <div className="vtm-field vtm-field--full">
                <label className="vtm-label">Street Address</label>
                <div className="vtm-value">{m.address || <span className="vtm-value--empty">—</span>}</div>
              </div>
              <Field label="Country" value={m.country?.country_name || m.country} />
              <Field label="State" value={m.state?.state_name || m.state} />
              <Field label="City" value={m.city?.city_name || m.city} />
              <Field label="Zip / Postal Code" value={m.zip_code} />
            </div>
          </div>

          {/* ───── SECTION 3: DOCUMENTS ───── */}
          <div className="vtm-section">
            <div className="vtm-section-header">
              <span className="vtm-section-badge">04</span>
              <h2 className="vtm-section-title">Documents</h2>
            </div>
            <div style={{ display: "flex", flexDirection: "column", gap: "12px" }}>
              {m.document_list?.length > 0 ? (
                m.document_list.map((doc, i) => (
                  <div key={i} className="vtm-doc-card">
                    <div className="vtm-doc-icon-wrap">
                      <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
                        <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
                        <polyline points="14 2 14 8 20 8" />
                        <line x1="16" y1="13" x2="8" y2="13" />
                        <line x1="16" y1="17" x2="8" y2="17" />
                      </svg>
                    </div>
                    <div className="vtm-doc-fields">
                      <div className="vtm-field">
                        <label className="vtm-label">Document Name</label>
                        <div className="vtm-value">{doc.name || <span className="vtm-value--empty">—</span>}</div>
                      </div>
                      {doc.url && (
                        <div className="vtm-field">
                          <label className="vtm-label">File</label>
                          <a href={doc.url} target="_blank" rel="noreferrer" className="vtm-doc-link">
                            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                              <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
                              <polyline points="7 10 12 15 17 10" />
                              <line x1="12" y1="15" x2="12" y2="3" />
                            </svg>
                            View Document
                          </a>
                        </div>
                      )}
                    </div>
                  </div>
                ))
              ) : (
                <p className="vtm-doc-empty">No documents uploaded.</p>
              )}
            </div>
          </div>

          {/* ───── SECTION 4: BANK DETAILS ───── */}
          <div className="vtm-section">
            <div className="vtm-section-header">
              <span className="vtm-section-badge">05</span>
              <h2 className="vtm-section-title">Bank Details</h2>
            </div>
            <div className="vtm-grid vtm-grid-2">
              <Field
                label="Account Number"
                value={
                  m.bank_details?.[0]?.acc_num
                    ? `${"*".repeat(Math.max(0, m.bank_details[0].acc_num.length - 4))}${m.bank_details[0].acc_num.slice(-4)}`
                    : ""
                }
              />
              <Field label="IFSC Code" value={m.bank_details?.[0]?.ifsc} />
              <Field label="Account Holder Name" value={m.bank_details?.[0]?.acc_holder_name} />
            </div>
          </div>

          {/* ── Footer ── */}
          {canEdit &&
            <div className="vtm-footer">
              <div className="vtm-footer-nav">
                <Link to={`/team/edit/${id}`} className="vtm-btn vtm-btn--primary">
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7" />
                    <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z" />
                  </svg>
                  Edit Member
                </Link>
              </div>
              <Link to="/team" className="vtm-btn vtm-btn--cancel">Back to Team</Link>
            </div>
          }
        </div>
      </div>
    </MainLayout>
  );
};

export default ViewTeamMember;